import React, { useState, useEffect, useCallback } from 'react';
import { FaShieldAlt, FaCalendarAlt, FaIdCard, FaStar, FaExclamationTriangle, FaCheckCircle, FaRedo, FaTimesCircle } from 'react-icons/fa';
import LoadingCard from './LoadingCard';
import { CardContainer, CardHeader, CardBody } from './CardStyles';
import api from '../../../../services/api.js';

const SPCCard = ({ idDm013, onDataUpdate }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [retrySuccess, setRetrySuccess] = useState(null);
  const [spcMissing, setSpcMissing] = useState(false);
  const [spcData, setSpcData] = useState({
    spcClass: '',
    spcMessage: '',
    spcProtocol: '',
    spcScore: '',
    spcRequestDate: ''
  });

  const fetchSPCData = useCallback(async () => {
    if (!idDm013) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const feasibilityResponse = await api.get(`feasibility_study?ID_DM013=${idDm013}`);
      const data = feasibilityResponse.data.data[0];
      
      if (feasibilityResponse.status === 200) {
        const hasSpcData = data.QT_SCORE_SPC !== null && data.DT_CONSULTA_SPC !== null;
        setSpcMissing(!hasSpcData);
        
        const updatedData = {
          spcClass: data.IN_RISCO_INADIMPLENCIA_SPC || 'Não informado',
          spcMessage: data.DS_MENSAGEM_SPC || 'Não informado',
          spcProtocol: data.ID_CONSULTA_SPC || 'Não informado',
          spcScore: data.QT_SCORE_SPC !== null ? data.QT_SCORE_SPC : 'Não informado',
          spcRequestDate: data.DT_CONSULTA_SPC || 'Não informado'
        };
        
        setSpcData(updatedData);
        onDataUpdate(updatedData);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Erro ao buscar dados do SPC:', error);
    }
  }, [idDm013, onDataUpdate]);

  useEffect(() => {
    fetchSPCData();
  }, [fetchSPCData]);

  const handleRetrySpc = async () => {
    setIsRetrying(true);
    setRetryError(null);
    setRetrySuccess(null);

    try {
      const response = await api.post('retry_spc', { ID_DM013: idDm013 });
      
      if (response.status === 200) {
        const result = response.data;
        setRetrySuccess(result.message);
        setSpcMissing(false);
        // Recarregar os dados
        await fetchSPCData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Erro ao refazer consulta SPC';
      setRetryError(errorMsg);
      console.error('Erro ao refazer consulta SPC:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Não informado') return 'Não informado';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };

  const getRiskClassColor = (riskClass) => {
    if (!riskClass || riskClass === 'Não informado') return '#6c757d';
    
    const classValue = riskClass.toString().toLowerCase();
    if (classValue.includes('baixo') || classValue.includes('low') || classValue === 'a') return '#28a745';
    if (classValue.includes('médio') || classValue.includes('medium') || classValue === 'b') return '#ffc107';
    if (classValue.includes('alto') || classValue.includes('high') || classValue === 'c') return '#dc3545';
    return '#6c757d';
  };

  const getScoreColor = (score) => {
    if (!score || score === 'Não informado') return '#6c757d';
    
    const scoreValue = parseInt(score);
    if (scoreValue >= 700) return '#28a745';
    if (scoreValue >= 500) return '#ffc107';
    if (scoreValue >= 300) return '#fd7e14';
    return '#dc3545';
  };

  if (isLoading) {
    return <LoadingCard title="Carregando Dados do SPC..." />;
  }

  if (!idDm013) {
    return <LoadingCard title="Aguardando dados do contrato..." />;
  }

  return (
    <CardContainer>
      <CardHeader>
        <h5>
          <FaShieldAlt style={{ marginRight: '10px' }} />
          Consulta SPC - Estudo #{idDm013}
        </h5>
      </CardHeader>
      <CardBody>
        <div style={{ marginBottom: '20px' }}>
          <h6 style={{ marginBottom: '15px', color: '#333' }}>Informações da Consulta</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <FaIdCard style={{ color: '#6c757d', marginRight: '8px' }} />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>Protocolo</span>
              </div>
              <span style={{ fontSize: '16px', color: '#212529' }}>{spcData.spcProtocol}</span>
            </div>
            
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <FaCalendarAlt style={{ color: '#6c757d', marginRight: '8px' }} />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>Data da Consulta</span>
              </div>
              <span style={{ fontSize: '16px', color: '#212529' }}>{formatDate(spcData.spcRequestDate)}</span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h6 style={{ marginBottom: '15px', color: '#333' }}>Análise de Risco</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <FaExclamationTriangle style={{ color: getRiskClassColor(spcData.spcClass), marginRight: '8px' }} />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>Classe de Risco</span>
              </div>
              <span style={{ 
                fontSize: '16px', 
                color: getRiskClassColor(spcData.spcClass),
                fontWeight: '600'
              }}>
                {spcData.spcClass}
              </span>
            </div>
            
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <FaStar style={{ color: getScoreColor(spcData.spcScore), marginRight: '8px' }} />
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#495057' }}>Score</span>
              </div>
              <span style={{ 
                fontSize: '16px', 
                color: getScoreColor(spcData.spcScore),
                fontWeight: '600'
              }}>
                {spcData.spcScore}
              </span>
            </div>
          </div>
        </div>

        {spcData.spcMessage && spcData.spcMessage !== 'Não informado' && (
          <div style={{ marginBottom: '20px' }}>
            <h6 style={{ marginBottom: '15px', color: '#333' }}>Observações SPC</h6>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '8px',
              border: '1px solid #b3d9ff'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '10px' 
              }}>
                <FaCheckCircle style={{ color: '#0066cc', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '14px', color: '#0066cc', lineHeight: '1.5' }}>
                  {spcData.spcMessage}
                </span>
              </div>
            </div>
          </div>
        )}

        {spcMissing && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '8px',
              border: '1px solid #ffc107',
              marginBottom: '15px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px' 
              }}>
                <FaExclamationTriangle style={{ color: '#856404', flexShrink: 0 }} />
                <span style={{ fontSize: '14px', color: '#856404', fontWeight: '600' }}>
                  A consulta SPC não foi realizada para este estudo. Clique no botão abaixo para realizar a consulta.
                </span>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleRetrySpc}
            disabled={isRetrying}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: isRetrying ? '#6c757d' : '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: isRetrying ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
          >
            <FaRedo style={{ animation: isRetrying ? 'spin 1s linear infinite' : 'none' }} />
            {isRetrying ? 'Consultando SPC...' : spcMissing ? 'Realizar Consulta SPC' : 'Refazer Consulta SPC'}
          </button>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>

        {retrySuccess && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#d4edda', 
            borderRadius: '8px',
            border: '1px solid #28a745',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaCheckCircle style={{ color: '#155724', flexShrink: 0 }} />
              <span style={{ fontSize: '14px', color: '#155724', fontWeight: '600' }}>
                {retrySuccess}
              </span>
            </div>
          </div>
        )}

        {retryError && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            borderRadius: '8px',
            border: '1px solid #dc3545',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaTimesCircle style={{ color: '#721c24', flexShrink: 0 }} />
              <span style={{ fontSize: '14px', color: '#721c24', fontWeight: '600' }}>
                {retryError}
              </span>
            </div>
          </div>
        )}
      </CardBody>
    </CardContainer>
  );
};

export default SPCCard;
